import { useCallback, useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import type { QrCodeRecord } from './types';

interface GetResult {
  data: QrCodeRecord | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useGetQrCode(id: string | null): GetResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  const [data, setData] = useState<QrCodeRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!id) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!isAuthenticated || accounts.length === 0) return;
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      setError('REACT_APP_API_URL is not set');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const tokenResponse = { accessToken: await acquireApiToken(instance, accounts[0]) };
        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        };
        const res = await fetch(
          `${apiUrl}/admin/qr-codes/${encodeURIComponent(id)}`,
          { headers },
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        if (!cancelled) {
          setData(json.data as QrCodeRecord);
          setError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load QR code';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, instance, accounts, id, tick]);

  return { data, loading, error, refetch };
}
